import React from "react";
import { useLang } from "@/lib/i18n";
import { Check, Star } from "lucide-react";

export default function Pricing({ settings }) {
  const { t } = useLang();
  const plans = t("pricing.plans");
  const prices = [settings.plan1_price, settings.plan2_price, settings.plan3_price];
  const testids = ["pricing-card-10days", "pricing-card-20days", "pricing-card-30days"];
  const btnIds = ["pricing-button-10days", "pricing-button-20days", "pricing-button-30days"];
  const selectPlan = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="pricing" className="py-20 lg:py-28 border-y border-[var(--border-gold)]" style={{ background: "var(--at-darkest)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="font-heading text-xs font-extrabold tracking-[0.25em] uppercase gold-gradient-text">07</span>
            <span className="w-8 h-px bg-[var(--trust-gold)]/50" />
            <span className="font-heading text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">{t("pricing.kicker")}</span>
          </div>
          <h2 className="font-serif-display font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">{t("pricing.title")}</h2>
          <p className="mt-4 text-base font-body text-white/75 leading-relaxed">{t("pricing.subtitle")}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {plans.map((plan, i) => {
            const featured = i === 2;
            return (
              <div
                key={i}
                data-testid={testids[i]}
                className={`relative flex flex-col rounded-3xl p-8 animate-fade-up transition-[transform] duration-300 ${featured ? "md:scale-105 md:z-10 corner-bracket" : "at-card-solid hover:-translate-y-1.5 hover:border-[var(--border-gold)]"}`}
                style={{
                  animationDelay: `${i * 0.08}s`,
                  ...(featured ? { background: "linear-gradient(160deg,#5B0715,#38040C)", border: "2px solid var(--trust-gold)", boxShadow: "0 0 40px rgba(255,215,0,0.2)" } : {}),
                }}
              >
                {featured && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 text-[11px] font-extrabold font-heading px-4 py-1.5 rounded-full text-[#38040C] whitespace-nowrap shadow-lg" style={{ background: "linear-gradient(135deg,#FFE566,#FFD700)" }}>
                    <Star size={12} fill="#38040C" /> {t("pricing.popular")}
                  </div>
                )}
                <h3 className="font-heading font-bold text-xl text-white">{plan.name}</h3>
                <div className="mt-4 flex items-baseline gap-1.5">
                  <span className={`font-serif-display font-bold text-6xl tracking-tight ${featured ? "gold-gradient-text" : "text-white"}`}>{prices[i]}</span>
                  <span className="text-sm font-body text-white/60">/ {plan.period}</span>
                </div>
                <ul className="mt-7 space-y-3 flex-1">
                  {plan.features.map((feat, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <span className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[#38040C]" style={{ background: "linear-gradient(135deg,#FFE566,#FFD700)" }}><Check size={12} /></span>
                      <span className="text-sm font-body text-white/85">{feat}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={btnIds[i]}
                  onClick={selectPlan}
                  className={`mt-8 w-full py-3.5 rounded-full font-bold font-heading text-sm transition-transform hover:-translate-y-0.5 ${featured ? "text-[#38040C] shadow-[0_10px_30px_rgba(255,215,0,0.3)]" : "border border-[var(--border-gold)] text-white hover:bg-white/5"}`}
                  style={featured ? { background: "linear-gradient(135deg,#FFE566,#FFD700,#D4AF37)" } : {}}
                >
                  {plan.button || t("pricing.choose")}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
